import { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, Loader2 } from "lucide-react";

/**
 * GooglePlacesInput - city/state/full-address autocomplete using Google Places.
 *
 * Required: REACT_APP_GOOGLE_MAPS_API_KEY in /app/frontend/.env
 *   Without the key, this component falls back to a plain text input so the
 *   user is never blocked. Selecting a place fires `onSelect(structured)`:
 *
 *   {
 *     formatted: "Los Angeles, CA, USA",
 *     street_1: "...",
 *     street_2: "",
 *     city: "Los Angeles",
 *     state: "CA",
 *     state_code: "CA",
 *     postal_code: "",
 *     country: "United States",
 *     country_code: "US",
 *     place_id: "ChIJ...",
 *     lat: 34.0522,
 *     lng: -118.2437,
 *     timezone: undefined,
 *   }
 *
 * Props:
 *   - value: string (formatted address shown in the input)
 *   - onChange(text): called as the user types (do NOT persist this - wait for onSelect)
 *   - onSelect(structured): called after the user picks a suggestion
 *   - mode: "city" (cities only - for /me users) | "address" (full street, for hosts/providers/travel bookings)
 *   - placeholder, disabled, className, dataTestId
 */
const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
let _scriptLoading = null;

function loadGoogleMaps() {
  if (typeof window === "undefined") return Promise.reject("ssr");
  if (window.google?.maps?.places) return Promise.resolve();
  if (!GMAPS_KEY) return Promise.reject("no-key");
  if (_scriptLoading) return _scriptLoading;
  _scriptLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_KEY}&libraries=places&v=weekly`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = (e) => reject(e);
    document.head.appendChild(s);
  });
  return _scriptLoading;
}

function pickComponent(components, type, useShort = false) {
  const c = components.find((cc) => (cc.types || []).includes(type));
  if (!c) return "";
  return useShort ? (c.short_name || c.long_name) : (c.long_name || c.short_name);
}

function structuredFromPlace(place) {
  const cc = place.address_components || [];
  return {
    formatted: place.formatted_address || "",
    street_1: [pickComponent(cc, "street_number"), pickComponent(cc, "route")].filter(Boolean).join(" "),
    street_2: pickComponent(cc, "subpremise"),
    city: pickComponent(cc, "locality") || pickComponent(cc, "postal_town") || pickComponent(cc, "sublocality"),
    state: pickComponent(cc, "administrative_area_level_1"),
    state_code: pickComponent(cc, "administrative_area_level_1", true),
    postal_code: pickComponent(cc, "postal_code"),
    country: pickComponent(cc, "country"),
    country_code: pickComponent(cc, "country", true),
    place_id: place.place_id,
    lat: place.geometry?.location?.lat?.() ?? place.geometry?.location?.lat,
    lng: place.geometry?.location?.lng?.() ?? place.geometry?.location?.lng,
  };
}

export const GooglePlacesInput = ({
  value = "",
  onChange,
  onSelect,
  mode = "city",
  placeholder,
  disabled = false,
  className = "",
  dataTestId = "google-places-input",
}) => {
  const inputRef = useRef(null);
  const acRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    loadGoogleMaps()
      .then(() => {
        if (!mounted || !inputRef.current || !window.google?.maps?.places) return;
        const opts = mode === "city"
          ? { types: ["(cities)"], fields: ["address_components", "geometry", "formatted_address", "place_id"] }
          : { types: ["address"],   fields: ["address_components", "geometry", "formatted_address", "place_id"] };
        // eslint-disable-next-line no-undef
        acRef.current = new window.google.maps.places.Autocomplete(inputRef.current, opts);
        acRef.current.addListener("place_changed", () => {
          const place = acRef.current.getPlace();
          if (!place || !place.place_id) return;
          const structured = structuredFromPlace(place);
          onSelect?.(structured);
          onChange?.(structured.formatted);
        });
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        if (e === "no-key") {
          setError("places-disabled");
        } else {
          setError(String(e));
        }
      });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const placeholderEffective = placeholder
    || (mode === "city" ? "Search city, state…" : "Search address…");

  return (
    <div className={`relative ${className}`}>
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholderEffective}
        disabled={disabled}
        data-testid={dataTestId}
        className="w-full h-11 pl-9 pr-9 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-50"
      />
      {loading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
      )}
      {error === "places-disabled" && (
        <div className="text-xs text-amber-700 mt-1" data-testid="places-disabled-notice">
          Places autocomplete is off - type your {mode === "city" ? "city, state" : "address"} manually.
        </div>
      )}
    </div>
  );
};

export default GooglePlacesInput;
